import type * as React from 'react';
import { XIcon } from 'lucide-react';

import { cn } from '../../lib/utils';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../shadcn/dialog';

/** How wide the pop-up opens from `sm` up. Below that it is the screen's width. */
const WIDTH = {
  /** Reading: a policy, a set of terms. */
  md: 'sm:max-w-xl',
  /** Working: a calendar beside its times. */
  lg: 'sm:max-w-4xl',
} as const;

export type ModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Wrapped in the dialog's trigger, so a click on it opens the pop-up. Leave out to open it yourself. */
  trigger?: React.ReactNode;
  title: React.ReactNode;
  /**
   * The line under the title. Always given to a screen reader, which announces
   * it with the title; shown on screen only with `showDescription`.
   */
  description: string;
  showDescription?: boolean;
  /** Beside the title, at the far end of its row: a language switch, a count. */
  headerAside?: React.ReactNode;
  /** The fixed row at the bottom: the pop-up's actions. Leave out for none. */
  footer?: React.ReactNode;
  width?: keyof typeof WIDTH;
  /** For a body that needs to know where it is scrolled to. */
  bodyRef?: React.Ref<HTMLDivElement>;
  onBodyScroll?: React.UIEventHandler<HTMLDivElement>;
  bodyClassName?: string;
  /** Turns the whole pop-up right to left, for Arabic. */
  dir?: 'ltr' | 'rtl';
  lang?: string;
  children: React.ReactNode;
};

/**
 * The site's pop-up: a header with the title, a body that scrolls, and an
 * optional footer for the actions — the frame the booking terms opened in,
 * made one component so every pop-up on the site is drawn the same way.
 *
 * Header and footer stay put and only the body scrolls, so a long body never
 * pushes the title or the buttons out of reach. A hairline rules each off from
 * the body. Built on the shadcn `Dialog`, which brings the focus trap, Escape,
 * the click outside and the overlay.
 */
export function Modal({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  showDescription = false,
  headerAside,
  footer,
  width = 'md',
  bodyRef,
  onBodyScroll,
  bodyClassName,
  dir,
  lang,
  children,
}: ModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}

      <DialogContent
        showCloseButton={false}
        dir={dir}
        lang={lang}
        className={cn('flex max-h-[85svh] flex-col gap-0 p-0', WIDTH[width])}
      >
        {/* `sm:text-start` too: shadcn's header sets `sm:text-left`, which a
            bare `text-start` does not replace, and would hold an Arabic title
            to the left. */}
        <DialogHeader className="border-b border-hairline px-6 pt-6 pb-4 text-start sm:text-start">
          {/* Title and aside on one row. The row mirrors with `dir`, as the
              rest of the pop-up does. */}
          <div className="flex items-center justify-between gap-4">
            <DialogTitle className="text-xl font-medium">{title}</DialogTitle>
            <div className="flex shrink-0 items-center gap-2">
              {headerAside}
              {/* A small × at the end of the title row, the quick way out
                  beside the footer's own buttons. Escape and a click outside
                  close it too. */}
              <DialogClose
                aria-label="Close"
                className={cn(
                  'inline-flex size-8 items-center justify-center rounded-button text-ink-muted',
                  'transition-colors hover:bg-raised hover:text-ink',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
                )}
              >
                <XIcon aria-hidden="true" className="size-4" />
              </DialogClose>
            </div>
          </div>
          <DialogDescription className={cn(!showDescription && 'sr-only')}>
            {description}
          </DialogDescription>
        </DialogHeader>

        <div
          ref={bodyRef}
          onScroll={onBodyScroll}
          className={cn('min-h-0 flex-1 overflow-y-auto px-6 py-4', bodyClassName)}
        >
          {children}
        </div>

        {/* On a phone the footer is a column (the dialog's own layout), and
            its buttons stretch to the full width: a thumb-sized target each.
            From `sm` they sit in a row at the end, centred on its line. */}
        {footer && (
          <DialogFooter className="border-t border-hairline px-6 py-4 sm:items-center">
            {footer}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
